/**
 * Bulk Update — the fill later schedule, and whether a row can take it.
 *
 * A schedule is an ordered list of `{ quantity, scheduledFor }`. The apply bar builds ONE and
 * stamps it across the selection (POM-6167: split in bulk, instead of one at a time), so the same
 * list is validated twice for different reasons: once on its own, before it touches any row, and
 * once per row, against that row's quantity.
 *
 * POM-6197 removed the lead time as a RULE. It survives only as the prefill the pickers open on
 * (see useFillLaterSchedule) — the operator may choose any day after today, including two fills on
 * the same day. Serialisation is the server's job: POM-6150 holds a scheduled row at
 * WAITING_SHIPMENT until every item on its prescription has shipped, so an early date parks rather
 * than jumping the queue.
 *
 * Every rule here has a server-side counterpart, named on each function. The server stays the
 * authority -- it re-reads each line item under lock -- and these exist so the operator sees the
 * problem while staging rather than as a failed row after commit.
 */

export const EMPTY_SCHEDULE_LINE = { quantity: '', scheduledFor: '' };

export const SCHEDULE_BLOCK = {
  EXCEEDS_QUANTITY: 'EXCEEDS_QUANTITY',
  PAST_DATE: 'PAST_DATE',
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MS_PER_DAY = 86400000;

/* Declared here rather than imported from bulkOverrideStaging: that module imports THIS one, and
 * a cycle between them would leave whichever loaded second holding an undefined binding. Same
 * rule as isStagedQuantity -- a dispensing count, so only a positive whole number. */
const isDispensableCount = (value) =>
  value !== '' &&
  value !== null &&
  value !== undefined &&
  Number.isInteger(Number(value)) &&
  Number(value) > 0;

/* Local noon, not midnight UTC: the browser's timezone shifts a midnight date across the day
 * boundary in either direction, and these dates are calendar days the pharmacy picked, never
 * instants. Noon leaves 12 hours of slack in both directions, so no offset on earth moves the day. */
const dayValue = (iso) => {
  if (!DATE_ONLY.test(iso || '')) return NaN;

  const [y, m, d] = iso.split('-').map(Number);
  const at = new Date(y, m - 1, d, 12, 0, 0, 0);

  if (at.getFullYear() !== y || at.getMonth() !== m - 1 || at.getDate() !== d) return NaN;

  return at.getTime();
};

export const isRealCalendarDay = (iso) => !Number.isNaN(dayValue(iso));

export const daysBetween = (fromIso, toIso) =>
  Math.round((dayValue(toIso) - dayValue(fromIso)) / MS_PER_DAY);

export const addDaysIso = (iso, days) => {
  const base = dayValue(iso);

  if (Number.isNaN(base)) return '';

  const at = new Date(base + days * MS_PER_DAY);
  const pad = (n) => String(n).padStart(2, '0');

  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
};

export const todayIso = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');

  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/**
 * "Sep 16" for a date this year, "Sep 16 2027" once it crosses into another — the year is noise
 * on the common case and essential on the rare one. Reads the plain YYYY-MM-DD the schedule
 * carries, NOT the midnight-UTC instant the queue's rows hold, so fillLaterDates' UTC helpers do
 * not apply here.
 */
export const formatScheduleDay = (iso, today = todayIso()) => {
  const at = dayValue(iso);

  if (Number.isNaN(at)) return '';

  const day = new Date(at);
  const label = `${MONTHS[day.getMonth()]} ${day.getDate()}`;

  return iso.slice(0, 4) === today.slice(0, 4) ? label : `${label} ${day.getFullYear()}`;
};

/** How far out the fill is, in the operator's words. Null once the date is not in the future,
 * which the schedule refuses anyway -- there is nothing useful to say about a date it will not
 * accept, and "0 days ago" beside a rejected row is just noise on top of the error. */
export const relativeScheduleDay = (iso, today = todayIso()) => {
  const days = daysBetween(today, iso);

  if (!Number.isFinite(days) || days < 1) return null;
  if (days === 1) return 'tomorrow';

  return `in ${days} days`;
};

/** A line is only part of the schedule once BOTH halves are set — a quantity with no date is not
 * committable, and counting it would let the footer offer a commit the server would refuse. The
 * date arrives prefilled, so in practice it is the quantity that completes a line. */
export const isCompleteLine = (line) =>
  isDispensableCount(line?.quantity) && isRealCalendarDay(line?.scheduledFor);

export const completeLines = (schedule) => (schedule || []).filter(isCompleteLine);

export const scheduleTotal = (schedule) =>
  completeLines(schedule).reduce((total, line) => total + Number(line.quantity), 0);

export const scheduleDates = (schedule) =>
  completeLines(schedule)
    .map((line) => line.scheduledFor)
    .sort();

/**
 * Is the schedule internally sound, before any row is considered?
 *
 * Mirrors validateSplitInputs, which since POM-6197 asks one thing of a date: that it is strictly
 * after today. Two lines may share a date — that creates two fills, which is the intent. Returns
 * the first problem found, keyed by line index so the apply bar can mark the offending field.
 */
export const scheduleProblem = (params) => {
  const { schedule, today = todayIso(), dispenseQuantity } = params || {};
  const lines = completeLines(schedule);

  if (lines.length === 0) return null;

  const inThePast = lines.findIndex((line) => line.scheduledFor <= today);

  if (inThePast !== -1) {
    return {
      reason: SCHEDULE_BLOCK.PAST_DATE,
      lineIndex: inThePast,
      message: 'Fill dates must be after today',
    };
  }

  /* The same rule rowScheduleProblem applies, checked here too whenever the bar knows what every
   * row holds -- which it does while the quantity filter is on, since that filter is an exact
   * match. Without it a schedule totalling more than the rows carry stamps cleanly and turns the
   * whole selection red afterwards, which is a worse way to learn it than being stopped. */
  const available = Number(dispenseQuantity);

  if (Number.isFinite(available) && available > 0) {
    const total = scheduleTotal(schedule);

    if (total >= available) {
      return {
        reason: SCHEDULE_BLOCK.EXCEEDS_QUANTITY,
        lineIndex: lines.length - 1,
        message: `Scheduling ${total} of ${available} leaves nothing to fill now`,
      };
    }
  }

  return null;
};

/**
 * Can THIS row take the schedule?
 *
 * The one check the schedule alone cannot make. Mirrors applySplitToLineItem's
 * `requestedTotal >= originalQuantity` — strictly less than, because the original ships before any
 * fill later order exists and so has to keep something to fill now.
 */
export const rowScheduleProblem = (params) => {
  const { schedule, currentQuantity } = params || {};

  const lines = completeLines(schedule);

  if (lines.length === 0) return null;

  const current = Number(currentQuantity);
  const total = scheduleTotal(schedule);

  if (!Number.isFinite(current) || total >= current) {
    return {
      reason: SCHEDULE_BLOCK.EXCEEDS_QUANTITY,
      message: Number.isFinite(current)
        ? `Scheduling ${total} of ${current} leaves nothing to fill now`
        : 'This row has no dispense quantity to split',
    };
  }

  return null;
};

/** What the original keeps once the whole schedule comes off. Null when the row cannot take it. */
export const retainedAfterSchedule = (params) => {
  const { schedule, currentQuantity } = params || {};

  if (rowScheduleProblem(params)) return null;
  if (completeLines(schedule).length === 0) return null;

  return Number(currentQuantity) - scheduleTotal(schedule);
};

/**
 * The rows a commit would actually send: selected, carrying a complete schedule, and not blocked.
 * Kept here rather than in the hook so the footer tally, the confirm summary and the payload all
 * read one answer instead of three that can disagree.
 */
export const partitionBySchedule = (params) => {
  const { rows, stagedValues } = params || {};

  return (rows || []).reduce(
    (acc, row) => {
      const schedule = stagedValues?.[row.lineItemId]?.splits;

      if (completeLines(schedule).length === 0) {
        acc.unscheduled.push(row.lineItemId);

        return acc;
      }

      const problem = rowScheduleProblem({ schedule, currentQuantity: row.quantity });

      if (problem) acc.blocked.push({ lineItemId: row.lineItemId, ...problem });
      else acc.ready.push(row.lineItemId);

      return acc;
    },
    { ready: [], blocked: [], unscheduled: [] }
  );
};
